import { parseAppEvent, type AppWsEvent } from "../api/appEvents.ts";
import type { Timers } from "../environment.tsx";

/** `unstable` — App WS не подключён дольше `UNSTABLE_AFTER_MS` или браузер офлайн. */
export type ConnectionState = "connected" | "unstable";

export const UNSTABLE_AFTER_MS = 5_000;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

export type RealtimeConnectionOptions = {
  /** Новый токен `/v1/auth/ws-token` на каждое подключение: backend гасит его при первом же использовании. */
  fetchToken: () => Promise<string>;
  openSocket: (token: string) => WebSocket;
  onEvent: (event: AppWsEvent) => void;
  /** Данные на экране могли устареть: связь восстановлена после обрыва или пропущены события. */
  onResync: () => void;
  onStateChange: (state: ConnectionState) => void;
  timers: Timers;
};

/**
 * Держит App WS открытым, пока его не остановят: обрыв — переподключение с экспоненциальным backoff
 * (1 с, 2 с, 4 с… до 30 с), возврат сети (`online`) — переподключение сразу. Возвращает `stop`.
 */
export function startRealtimeConnection(options: RealtimeConnectionOptions): () => void {
  const { timers } = options;
  let stopped = false;
  let socket: WebSocket | null = null;
  let connected = false;
  let connectedBefore = false;
  let attempt = 0;
  let lastSeq: number | null = null;
  let reconnectTimer: number | undefined;
  let unstableTimer: number | undefined;
  let disconnectedTooLong = false;
  // `null` — состояние ещё не сообщали: первое `updateState` сообщает его всегда, даже после прежнего соединения.
  let state: ConnectionState | null = null;

  function updateState(): void {
    const next = !navigator.onLine || (!connected && disconnectedTooLong) ? "unstable" : "connected";
    if (next === state) return;
    state = next;
    options.onStateChange(state);
  }

  function startUnstableTimer(): void {
    if (unstableTimer !== undefined || disconnectedTooLong) return;
    unstableTimer = timers.setTimeout(() => {
      unstableTimer = undefined;
      disconnectedTooLong = true;
      updateState();
    }, UNSTABLE_AFTER_MS);
  }

  function scheduleReconnect(): void {
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
    attempt += 1;
    reconnectTimer = timers.setTimeout(() => void connect(), delay);
  }

  async function connect(): Promise<void> {
    reconnectTimer = undefined;
    let token: string;
    try {
      token = await options.fetchToken();
    } catch {
      // Сеть или backend недоступны: пробуем снова, как после обрыва сокета.
      if (!stopped) scheduleReconnect();
      return;
    }
    if (stopped) return;
    const current = options.openSocket(token);
    socket = current;
    current.addEventListener("open", () => {
      if (stopped || socket !== current) return;
      connected = true;
      attempt = 0;
      lastSeq = null;
      disconnectedTooLong = false;
      timers.clearTimeout(unstableTimer);
      unstableTimer = undefined;
      updateState();
      // Пока связи не было, события терялись. Backend и сам присылает `sync_required` после каждого подключения,
      // но переподключение не должно зависеть от него. Первое подключение обходится серверным `sync_required`.
      if (connectedBefore) options.onResync();
      connectedBefore = true;
    });
    current.addEventListener("message", (message) => {
      if (stopped || socket !== current) return;
      const event = parseAppEvent((message as MessageEvent).data);
      if (!event) return;
      if (lastSeq !== null && event.seq > lastSeq + 1) options.onResync();
      lastSeq = Math.max(lastSeq ?? event.seq, event.seq);
      options.onEvent(event);
    });
    current.addEventListener("close", () => {
      if (stopped || socket !== current) return;
      socket = null;
      connected = false;
      startUnstableTimer();
      updateState();
      scheduleReconnect();
    });
  }

  function handleOnline(): void {
    updateState();
    // Сеть вернулась: ждать очередного шага backoff незачем.
    if (reconnectTimer === undefined) return;
    timers.clearTimeout(reconnectTimer);
    attempt = 0;
    void connect();
  }

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", updateState);
  startUnstableTimer();
  updateState();
  void connect();

  return () => {
    stopped = true;
    timers.clearTimeout(reconnectTimer);
    timers.clearTimeout(unstableTimer);
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", updateState);
    socket?.close();
    socket = null;
  };
}
